"use client";
import Link from "next/link";
import { ArrowRight, Newspaper } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { latestNews as data } from "@/lib/data/press";

const accent = "var(--color-purple)";

export function LatestNews() {
  return (
    <Section id="latest-news" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Date</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Headline</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {data.headlines.map((h) => (
                    <tr key={h.headline} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top font-mono text-xs" style={{ color: accent }}>{h.date}</td>
                      <td className="px-4 py-3 align-top font-medium">{h.headline}</td>
                      <td className="px-4 py-3 align-top hidden md:table-cell text-[rgb(var(--fg-muted))]">{h.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                <Newspaper className="h-4 w-4" style={{ color: accent }} />
              </span>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Platform Milestones</span>
            </div>
            <ul className="mt-4 space-y-2">
              {data.milestones.map((m, i) => (
                <li key={i} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                  <span className="font-semibold" style={{ color: accent }}>{m.quarter}</span> — {m.description}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={400}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
